// Split out of InstanceDetailPage.tsx, then migrated onto components/ui.

import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useBackups, useCreateBackup, useUploadBackup, useDeleteBackup, useDownloadBackup, useRestoreBackup, useReplaceFilestore, useImportOdooBackup, formatBytes } from '../../hooks/useBackups'
import { useAuthStore } from '../../store/authStore'
import type { Backup, Instance } from '../../api/client'
import { extractApiError } from './shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  LinkButton,
} from '../../components/ui'
import type { BadgeTone } from '../../components/ui'

// The four trigger colours were blue / purple / amber / gray. The palette
// contract has six roles and no purple, so `auto` and `upload` both land on
// accent — the label inside the pill is what tells them apart.
const TRIGGER_TONE: Record<Backup['trigger'], BadgeTone> = {
  manual: 'neutral',
  auto: 'accent',
  upload: 'accent',
  pre_op: 'warning',
}

const STATUS_TONE: Record<Backup['status'], BadgeTone> = {
  done: 'success',
  failed: 'danger',
  running: 'warning',
}

export function BackupsTab({ instance }: { instance: Instance }) {
  const rawPlan = useAuthStore((s) => s.plan)
  const role = useAuthStore((s) => s.role)
  const plan = rawPlan ?? 'free'
  const isFreePlan = plan === 'free' && role !== 'admin'
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: backups, isLoading } = useBackups(instance.id)
  const createMutation = useCreateBackup(instance.id)
  const uploadMutation = useUploadBackup(instance.id)
  const deleteMutation = useDeleteBackup(instance.id)
  const downloadMutation = useDownloadBackup(instance.id)
  const restoreMutation = useRestoreBackup(instance.id)
  const replaceFilestoreMutation = useReplaceFilestore(instance.id)
  const importMutation = useImportOdooBackup(instance.id)
  const [createError, setCreateError] = useState('')
  const [uploadError, setUploadError] = useState('')
  const [downloadError, setDownloadError] = useState('')
  const [deleteBackupError, setDeleteBackupError] = useState('')
  const [confirmRestoreId, setConfirmRestoreId] = useState<string | null>(null)
  const [neutralizeOnRestore, setNeutralizeOnRestore] = useState(false)
  const [restoreError, setRestoreError] = useState('')
  const [restoreStarted, setRestoreStarted] = useState(false)
  const [replaceFilestoreError, setReplaceFilestoreError] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const filestoreInputRef = useRef<HTMLInputElement>(null)

  const isRestoring = instance.status === 'restoring'
  const isRestoreInProgress = restoreStarted || isRestoring
  const restoreTarget = backups?.find((b) => b.id === confirmRestoreId) ?? null
  // Both of these used to fire on a single click. Deleting a backup and
  // overwriting the live filestore are irreversible, so each is gated now.
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const deleteTarget = backups?.find((b) => b.id === confirmDeleteId) ?? null
  const [pendingFilestore, setPendingFilestore] = useState<File | null>(null)
  // Same staged-then-confirmed shape as the filestore replace above: picking the
  // file must not start an operation that drops the instance's database.
  const [pendingImport, setPendingImport] = useState<File | null>(null)
  const [neutralizeOnImport, setNeutralizeOnImport] = useState(true)
  const [importError, setImportError] = useState('')
  const importInputRef = useRef<HTMLInputElement>(null)

  // Clear restoreStarted once the server confirms restoring or hits an error
  useEffect(() => {
    if (isRestoring || instance.status === 'error') setRestoreStarted(false)
  }, [isRestoring, instance.status])

  const handleCreate = () => {
    setCreateError('')
    createMutation.mutate(undefined, {
      onError: (e) => setCreateError(extractApiError(e) ?? t('detail.backups.createFailed', { defaultValue: 'Backup failed' })),
    })
  }

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadError('')
    uploadMutation.mutate(file, {
      onError: (err) => setUploadError(extractApiError(err) ?? t('detail.backups.uploadFailed')),
      onSettled: () => { if (fileInputRef.current) fileInputRef.current.value = '' },
    })
  }

  // Picking the file no longer starts the upload — it stages it for confirmation.
  const handleReplaceFilestore = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setReplaceFilestoreError('')
    setPendingFilestore(file)
  }

  const clearFilestoreInput = () => {
    if (filestoreInputRef.current) filestoreInputRef.current.value = ''
  }

  const confirmReplaceFilestore = () => {
    if (!pendingFilestore) return
    replaceFilestoreMutation.mutate(pendingFilestore, {
      onError: (err) => setReplaceFilestoreError(extractApiError(err) ?? t('detail.backups.replaceFilestoreFailed')),
      onSettled: () => {
        setPendingFilestore(null)
        clearFilestoreInput()
      },
    })
  }

  const clearImportInput = () => {
    if (importInputRef.current) importInputRef.current.value = ''
  }

  const handlePickImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setImportError('')
    setPendingImport(file)
  }

  const confirmImport = () => {
    if (!pendingImport) return
    importMutation.mutate(
      { file: pendingImport, neutralize: neutralizeOnImport },
      {
        onSuccess: () => setRestoreStarted(true),
        onError: (err) => setImportError(extractApiError(err) ?? t('detail.backups.importFailed')),
        onSettled: () => {
          setPendingImport(null)
          clearImportInput()
        },
      },
    )
  }

  const handleDownload = (backupId: string, filename: string, part: 'db' | 'filestore') => {
    setDownloadError('')
    downloadMutation.mutate({ backupId, filename, part }, {
      onError: (e) => setDownloadError(extractApiError(e) ?? t('detail.backups.downloadFailed', { defaultValue: 'Download failed' })),
    })
  }

  if (isFreePlan) {
    return (
      <Card padded={false} className="overflow-hidden">
        <div className="relative px-6 py-10 bg-gradient-to-br from-brand-50 via-white to-blue-50 border-b border-gray-100 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white border border-brand-200 shadow-sm mb-3">
            <svg className="w-6 h-6 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 11c0-1.657-1.79-3-4-3s-4 1.343-4 3 1.79 3 4 3 4-1.343 4-3zm0 0v6c0 1.657 1.79 3 4 3s4-1.343 4-3-1.79-3-4-3M12 11V5c0-1.657 1.79-3 4-3s4 1.343 4 3-1.79 3-4 3" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900">{t('detail.backups.teaser.title')}</h2>
          <p className="text-sm text-gray-600 mt-1.5 max-w-md mx-auto">{t('detail.backups.teaser.subtitle')}</p>
          <Button onClick={() => navigate('/billing')} className="mt-5 font-semibold px-5 py-2.5 shadow-sm">
            {t('detail.backups.teaser.cta')}
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </Button>
        </div>
        <div className="px-6 py-6 grid sm:grid-cols-2 gap-4">
          {(['scheduled', 'oneClickRestore', 'download', 'filestoreReplace'] as const).map((k) => (
            <div key={k} className="flex gap-3">
              <div className="shrink-0 mt-0.5 w-5 h-5 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{t(`detail.backups.teaser.features.${k}.title`)}</p>
                <p className="text-xs text-gray-500 mt-0.5">{t(`detail.backups.teaser.features.${k}.body`)}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {isRestoreInProgress && (
        <Alert tone="warning" className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse shrink-0" />
            <span>
              {t('detail.backups.restoringBanner')}
              {instance.status_message && (
                <span className="text-xs text-amber-600 ml-1.5">
                  ({t(`status.msg.${instance.status_message}`, instance.status_message)})
                </span>
              )}
            </span>
          </div>
          <LinkButton
            tone="neutral"
            onClick={() => navigate(`/instances/${instance.id}/logs`)}
            className="text-amber-700 hover:text-amber-800 underline underline-offset-2 shrink-0"
          >
            {t('detail.backups.viewLogs', { defaultValue: 'View logs' })}
          </LinkButton>
        </Alert>
      )}

      {instance.status === 'error' && instance.status_message && (
        <Alert tone="danger" title={t('detail.backups.restoreFailedBanner')}>
          <p className="text-xs break-words">{instance.status_message}</p>
        </Alert>
      )}

      <CardHeader
        title={t('detail.backups.heading')}
        description={t('detail.backups.subheading')}
        actions={
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".dump,.sql"
              className="hidden"
              onChange={handleUpload}
            />
            <div className="flex items-center gap-1.5">
              <Button
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={isRestoreInProgress}
                loading={uploadMutation.isPending}
              >
                {uploadMutation.isPending ? t('detail.backups.uploading') : t('detail.backups.upload')}
              </Button>
              {/* Stays an <a>: it opens the docs in a new tab, it is not an action. */}
              <a
                href="/docs#migrate-2"
                target="_blank"
                rel="noopener noreferrer"
                title={t('detail.backups.uploadHint')}
                className="text-gray-400 hover:text-brand-600 transition-colors"
              >
                <svg className="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </a>
            </div>
            <Button
              onClick={handleCreate}
              disabled={instance.status !== 'running'}
              loading={createMutation.isPending}
            >
              {createMutation.isPending ? t('detail.backups.creating') : t('detail.backups.create')}
            </Button>
          </>
        }
      />

      {createError && <Alert tone="danger">{createError}</Alert>}
      {uploadError && <Alert tone="danger">{uploadError}</Alert>}
      {downloadError && <Alert tone="danger">{downloadError}</Alert>}

      {isLoading ? (
        <div className="text-sm text-gray-400">{t('detail.backups.loading')}</div>
      ) : !backups || backups.length === 0 ? (
        <Card padded={false}>
          <EmptyState title={t('detail.backups.empty')} />
        </Card>
      ) : (
        <Card padded={false} className="divide-y divide-gray-100">
          {backups.map((backup) => (
            <div key={backup.id} className="flex items-center justify-between px-5 py-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{backup.filename}</p>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className="text-xs text-gray-400">{new Date(backup.created_at).toLocaleString()}</span>
                  <span title={backup.trigger === 'pre_op' ? t('detail.backups.preOpHint', { defaultValue: 'Safety-net backup before a risky operation. Auto-deleted after 24h.' }) : undefined}>
                    <Badge tone={TRIGGER_TONE[backup.trigger] ?? 'neutral'}>{backup.trigger}</Badge>
                  </span>
                  <Badge tone={STATUS_TONE[backup.status] ?? 'warning'}>{backup.status}</Badge>
                  {backup.size_bytes > 0 && (
                    <span className="text-xs text-gray-400">{formatBytes(backup.size_bytes)}</span>
                  )}
                </div>
                {backup.error && (
                  <p className="text-xs text-red-500 mt-0.5 truncate">{backup.error}</p>
                )}
              </div>
              <div className="flex items-center gap-2 ml-3 shrink-0">
                {backup.status === 'done' && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownload(backup.id, backup.filename, 'db')}
                      disabled={downloadMutation.isPending}
                    >
                      {t('detail.backups.downloadDb')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownload(backup.id, backup.filename.replace(/\.dump$/, '.filestore.tar.gz'), 'filestore')}
                      disabled={downloadMutation.isPending}
                    >
                      {t('detail.backups.downloadFilestore')}
                    </Button>
                    <Button
                      variant="warning"
                      size="sm"
                      onClick={() => { setRestoreError(''); setConfirmRestoreId(backup.id) }}
                      disabled={restoreMutation.isPending || isRestoreInProgress || (instance.status !== 'running' && instance.status !== 'stopped')}
                    >
                      {t('detail.backups.restore')}
                    </Button>
                  </>
                )}
                <LinkButton
                  tone="danger"
                  onClick={() => {
                    setDeleteBackupError('')
                    setConfirmDeleteId(backup.id)
                  }}
                  disabled={deleteMutation.isPending}
                >
                  {t('detail.backups.delete')}
                </LinkButton>
              </div>
            </div>
          ))}
        </Card>
      )}
      {deleteBackupError && <Alert tone="danger">{deleteBackupError}</Alert>}
      {restoreError && <Alert tone="danger">{restoreError}</Alert>}

      {deleteTarget && (
        <ConfirmDialog
          title={t('detail.backups.deleteConfirm')}
          message={
            <>
              <span className="block break-all font-medium">{deleteTarget.filename}</span>
              <span className="mt-2 block">{t('detail.backups.deleteConfirmBody')}</span>
            </>
          }
          confirmLabel={t('detail.backups.deleteYes')}
          busy={deleteMutation.isPending}
          onConfirm={() =>
            deleteMutation.mutate(deleteTarget.id, {
              onSuccess: () => setConfirmDeleteId(null),
              onError: (e) => {
                setDeleteBackupError(extractApiError(e) ?? t('detail.backups.deleteError'))
                setConfirmDeleteId(null)
              },
            })
          }
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}

      {pendingFilestore && (
        <ConfirmDialog
          title={t('detail.backups.replaceFilestoreConfirm')}
          message={
            <>
              <span className="block break-all font-medium">{pendingFilestore.name}</span>
              <span className="mt-2 block">{t('detail.backups.replaceFilestoreConfirmBody')}</span>
            </>
          }
          confirmLabel={t('detail.backups.replaceFilestoreYes')}
          busy={replaceFilestoreMutation.isPending}
          onConfirm={confirmReplaceFilestore}
          onCancel={() => {
            setPendingFilestore(null)
            clearFilestoreInput()
          }}
        />
      )}

      {restoreTarget && (
        <ConfirmDialog
          title={t('detail.backups.restoreConfirm')}
          message={
            <>
              <span className="block break-all">{restoreTarget.filename}</span>
              <Checkbox
                className="mt-3"
                label={t('detail.backups.neutralize')}
                title={t('detail.backups.neutralizeHint')}
                checked={neutralizeOnRestore}
                onChange={(e) => setNeutralizeOnRestore(e.target.checked)}
              />
            </>
          }
          confirmLabel={restoreMutation.isPending ? t('detail.backups.restoring') : t('detail.backups.restoreYes')}
          cancelLabel={t('detail.backups.restoreNo')}
          busy={restoreMutation.isPending}
          onConfirm={() => {
            setRestoreError('')
            restoreMutation.mutate(
              { backupId: restoreTarget.id, neutralize: neutralizeOnRestore },
              {
                onSuccess: () => {
                  setRestoreStarted(true)
                  setConfirmRestoreId(null)
                  setNeutralizeOnRestore(false)
                },
                onError: (e) => {
                  setRestoreError(extractApiError(e) ?? t('detail.backups.restoreFailed'))
                  setConfirmRestoreId(null)
                  setNeutralizeOnRestore(false)
                },
              },
            )
          }}
          onCancel={() => { setConfirmRestoreId(null); setNeutralizeOnRestore(false) }}
        />
      )}

      {instance.status === 'replacing_filestore' && (
        <Alert tone="warning" className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse shrink-0" />
          {t('detail.backups.replacingFilestoreBanner')}
        </Alert>
      )}

      <Card className="space-y-3">
        <CardHeader
          title={t('detail.backups.replaceFilestoreHeading')}
          description={t('detail.backups.replaceFilestoreHint')}
        />
        <input
          ref={filestoreInputRef}
          type="file"
          accept=".tar.gz,.tgz,.gz,application/gzip,application/x-gzip,application/x-compressed-tar"
          className="hidden"
          onChange={handleReplaceFilestore}
        />
        <Button
          variant="secondary"
          onClick={() => filestoreInputRef.current?.click()}
          disabled={instance.status === 'replacing_filestore' || (instance.status !== 'running' && instance.status !== 'stopped')}
          loading={replaceFilestoreMutation.isPending}
        >
          {replaceFilestoreMutation.isPending ? t('detail.backups.replaceFilestoreUploading') : t('detail.backups.replaceFilestoreBtn')}
        </Button>
        {replaceFilestoreError && <Alert tone="danger">{replaceFilestoreError}</Alert>}
      </Card>

      <Card className="space-y-3">
        <CardHeader
          title={t('detail.backups.importHeading')}
          description={t('detail.backups.importHint')}
        />
        <input
          ref={importInputRef}
          type="file"
          accept=".zip,application/zip,application/x-zip-compressed"
          className="hidden"
          onChange={handlePickImport}
        />
        <Button
          variant="secondary"
          onClick={() => importInputRef.current?.click()}
          disabled={isRestoreInProgress || (instance.status !== 'running' && instance.status !== 'stopped')}
          loading={importMutation.isPending}
        >
          {importMutation.isPending ? t('detail.backups.importUploading') : t('detail.backups.importBtn')}
        </Button>
        {importError && <Alert tone="danger">{importError}</Alert>}
      </Card>

      {pendingImport && (
        <ConfirmDialog
          title={t('detail.backups.importConfirm')}
          message={
            <>
              <span className="block break-all font-medium">{pendingImport.name}</span>
              <span className="mt-2 block">{t('detail.backups.importConfirmBody')}</span>
              <Checkbox
                className="mt-3"
                label={t('detail.backups.neutralize')}
                title={t('detail.backups.neutralizeHint')}
                checked={neutralizeOnImport}
                onChange={(e) => setNeutralizeOnImport(e.target.checked)}
              />
            </>
          }
          confirmLabel={t('detail.backups.importYes')}
          busy={importMutation.isPending}
          onConfirm={confirmImport}
          onCancel={() => {
            setPendingImport(null)
            clearImportInput()
          }}
        />
      )}
    </div>
  )
}
